import { inject, Injectable } from '@angular/core';
import { UserRole } from '../core/enums/user-roles.enum';
import { AuthenticationService } from './authentication.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly ROLE_KEY = 'userRole';
  private authenticationService = inject(AuthenticationService);

  /**
   * Guarda el rol del usuario en localStorage
   * @param role - Rol del usuario
   */
  setUserRole(role: UserRole): void {
    localStorage.setItem(this.ROLE_KEY, role);
  }

  /**
   * Obtiene el rol del usuario desde localStorage
   * @returns El rol del usuario o UserRole.GUEST si no existe
   */
  getUserRole(): UserRole {
    return (localStorage.getItem(this.ROLE_KEY) as UserRole);
  }

  /**
   * Verifica si el usuario tiene un rol específico
   * @param role - Un rol o array de roles permitidos
   * @returns true si el usuario tiene el rol, false en caso contrario
   */
  hasRole(role: UserRole | UserRole[]): boolean {
    const userRole = this.getUserRole();
    return Array.isArray(role) ? role.includes(userRole) : role === userRole;
  }

  /**
   * Limpia el rol del usuario del localStorage
   */
  clearUserRole(): void {
    localStorage.removeItem(this.ROLE_KEY);
  }

  /**
   * Verifica si el usuario está autenticado (tiene un rol válido)
   * @returns true si tiene un rol que existe en el enum UserRole
   */
  isAuthenticated(): boolean {
    const role = this.getUserRole();
    const validRoles = Object.values(UserRole);
    return validRoles.includes(role);
  }

  obtenerDefaultAreaId(areas: any[]): number | undefined {
    const userRole = this.getUserRole();
    if (!userRole || !areas?.length) return undefined;

    if (userRole === UserRole.CONSULTOR) {
      const areaConsultora = areas.find((area: any) =>
        area.areaNombre?.toLowerCase() === 'consultora'
      );
      return areaConsultora?.areaId;
    }
    return undefined;
  }

  /**
   * Obtiene el nombre del usuario actual para usarlo como Responsable Proceso
   * cuando el rol es CONSULTOR.
   * @returns El nombre del usuario o undefined si no aplica
   */
  obtenerDefaultResponsable(): string | undefined {
    const userRole = this.getUserRole();
    if (userRole !== UserRole.CONSULTOR) return undefined;

    const usuario = this.authenticationService.currentUserValue;
    const nombre = usuario?.nombres || '';

    return nombre?.trim() || undefined;
  }
}
